/**
 * Zustand Global State Store
 *
 * Purpose: Centralized state management for the Super Admin Dashboard
 * Features: User state, notifications, UI preferences, WebSocket connection status
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// User state interface
interface User {
  id: number;
  email: string;
  name: string;
  role: string;
  isAuthenticated: boolean;
}

// Notification interface
interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
}

// Dashboard preferences interface
interface DashboardPreferences {
  timeRange: '30d' | '90d' | '1y';
  autoRefresh: boolean;
  refreshInterval: number; // in seconds
  compactMode: boolean;
  theme: 'light' | 'dark';
}

// Store state interface
interface DashboardState {
  // User state
  user: User | null;
  setUser: (user: User | null) => void;
  logout: () => void;

  // Notifications
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  markNotificationRead: (id: string) => void;
  clearNotifications: () => void;

  // Dashboard preferences
  preferences: DashboardPreferences;
  updatePreferences: (preferences: Partial<DashboardPreferences>) => void;

  // WebSocket connection status
  isConnected: boolean;
  setConnectionStatus: (status: boolean) => void;

  // Alert filters
  alertFilter: 'all' | 'critical' | 'high' | 'medium' | 'info';
  setAlertFilter: (filter: 'all' | 'critical' | 'high' | 'medium' | 'info') => void;

  // Active tab tracking
  activeSecurityTab: 'breaches' | 'incidents';
  setActiveSecurityTab: (tab: 'breaches' | 'incidents') => void;

  activeComplianceTab: 'iso27001' | 'gdpr';
  setActiveComplianceTab: (tab: 'iso27001' | 'gdpr') => void;

  // Loading states
  isRefreshing: boolean;
  setIsRefreshing: (status: boolean) => void;
}

// Create the store with persistence
export const useDashboardStore = create<DashboardState>()(
  persist(
    (set, _get) => ({
      // User state
      user: null,
      setUser: (user) => set({ user }),
      logout: () => set({ user: null, notifications: [] }),

      // Notifications
      notifications: [],
      addNotification: (notification) => {
        const newNotification: Notification = {
          ...notification,
          id: `notif-${Date.now()}-${Math.random()}`,
          timestamp: new Date(),
          read: false,
        };
        set((state) => ({
          notifications: [newNotification, ...state.notifications].slice(0, 50), // Keep only last 50
        }));
      },
      markNotificationRead: (id) => {
        set((state) => ({
          notifications: state.notifications.map((notif) =>
            notif.id === id ? { ...notif, read: true } : notif
          ),
        }));
      },
      clearNotifications: () => set({ notifications: [] }),

      // Dashboard preferences
      preferences: {
        timeRange: '30d',
        autoRefresh: true,
        refreshInterval: 30, // 30 seconds
        compactMode: false,
        theme: 'light',
      },
      updatePreferences: (newPreferences) => {
        set((state) => ({
          preferences: { ...state.preferences, ...newPreferences },
        }));
      },

      // WebSocket connection status
      isConnected: false,
      setConnectionStatus: (status) => set({ isConnected: status }),

      // Alert filters
      alertFilter: 'all',
      setAlertFilter: (filter) => set({ alertFilter: filter }),

      // Active tab tracking
      activeSecurityTab: 'breaches',
      setActiveSecurityTab: (tab) => set({ activeSecurityTab: tab }),

      activeComplianceTab: 'iso27001',
      setActiveComplianceTab: (tab) => set({ activeComplianceTab: tab }),

      // Loading states
      isRefreshing: false,
      setIsRefreshing: (status) => set({ isRefreshing: status }),
    }),
    {
      name: 'dashboard-storage', // unique name for localStorage key
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Only persist these fields
        user: state.user,
        preferences: state.preferences,
        alertFilter: state.alertFilter,
        activeSecurityTab: state.activeSecurityTab,
        activeComplianceTab: state.activeComplianceTab,
      }),
    }
  )
);

// Selector hooks for optimized re-renders
export const useUser = () => useDashboardStore((state) => state.user);
export const usePreferences = () => useDashboardStore((state) => state.preferences);
export const useNotifications = () => useDashboardStore((state) => state.notifications);
export const useUnreadNotificationCount = () =>
  useDashboardStore((state) => state.notifications.filter((n) => !n.read).length);
export const useConnectionStatus = () => useDashboardStore((state) => state.isConnected);
export const useAlertFilter = () => useDashboardStore((state) => state.alertFilter);
export const useIsRefreshing = () => useDashboardStore((state) => state.isRefreshing);
